/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-import-assign */
/* eslint-disable import/namespace */
import React, { useEffect } from 'react';
import VisitsPage from './VisitsPage';
import * as useFetchVisitsModule from '../../componentsV2/Visits/useFetchVisits';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Function to generate dummy data for visits dynamically
const generateDummyVisits = (count) => {
  const visits = [];
  for (let i = 1; i <= count; i += 1) {
    visits.push({
      id: i,
      site_name: `Site ${i}`,
      date: `2024-11-${String(i).padStart(2, '0')}`,
      purpose: i % 2 === 0 ? 'Routine Visit' : 'Follow-Up Visit',
      workers_present: Math.floor(Math.random() * 50),
      workers_reached: Math.floor(Math.random() * 50),
      completed: i % 3 === 0,
      leader: `Leader ${i}`,
    });
  }
  return visits;
};

const totalVisits = 100; // Total number of visits
const dummyVisits = generateDummyVisits(totalVisits);
const dummyVisitsForEmptyVisits = []; // Empty data for EmptyVisitsPage

// Mock for DefaultVisitsPage
const mockUseFetchVisitsWithDummyData = (organizationId, params, currentPage, itemsPerPage, setTotalVisits) => {
  const offset = (currentPage - 1) * itemsPerPage;
  const paginatedVisits = dummyVisits.slice(offset, offset + itemsPerPage);
  // Simulate setting total visits in the component
  useEffect(() => {
    setTotalVisits(totalVisits);
  }, [setTotalVisits]);

  return paginatedVisits;
};

// Mock for EmptyVisitsPage
const mockUseFetchVisitsWithEmptyData = (organizationId, params, currentPage, itemsPerPage, setTotalVisits) => {
  useEffect(() => {
    setTotalVisits(0);
  }, [setTotalVisits]);

  return dummyVisitsForEmptyVisits;
};

// Override useFetchVisits with dummy data
useFetchVisitsModule.default = mockUseFetchVisitsWithDummyData;

export default {
  title: 'Pages/VisitsPage',
  component: VisitsPage,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nVisitsPage is the main page for recording and managing outreach visits.
        \n\n####_***It allows users to:***_
        \n- Log and review visit details, including site interactions, workers present, and worker outreach.
        \n- Switch between \`table\` and \`gallery\` views for better data visualization.
        \n- Navigate paginated results.
        \n\n- Search and filter visits based on various criteria like:
        \n>- **Keyword**
        \n>- **Date Range**
        \n>- **Purpose**
        \n>- **Workers Present/Reached**
        \n>- **Status** (All, Completed, Attempted).\n\n`,
      // When & how to use it?

      `\n\nThis page is used by \`planners\` and \`outreach\` workers to record and review visit details, including site interactions, worker data, and visit purposes. It integrates search, sorting, and view-switching functionalities between two views: \`table\` view and \`gallery\` view for an efficient user experience.
        \n\n###_**Key Features**_
        
        \n\n**Search Functionality**: 
        \n- Powered by the \`Search\` component with fields for keyword, date range, purpose, workers present/reached, and status.
        \n- Uses the \`SearchChipsCarousel\` for displaying active filters.
        
        \n\n**Breadcrumb Navigation**: 
        \n- Utilizes the \`Breadcrumbs\` component for providing navigation context.
        
        \n\n**View Modes**: 
        \n- Toggle between \`table\` and \`gallery\` views using \`handleModeChange\` function.
        
        \n\n**Sorting**: 
        \n- Enabled for table view through sortable column headers.
        
        \n\n**Pagination**: 
        \n- Supports paginated navigation via the \`Pagination\` component for both views.
        
        \n\n**Empty State**: 
        \n- Uses \`EmptyState\` to handle scenarios where no visits are available.\n\n`,
      // Current Usage
      `\nAccess to the page depends on the user role: planners can create, view and edit visits on web and mobile apps, while outreach workers can create visits for sites where they are conducting outreach, and edit or delete those they own, and also can view all visits for their organization only on mobile app
        \n\n###_**Access**_
        \n- Accessible on both web and mobile platforms (based on user roles).
        \n- Supports role-specific permissions for creating, viewing, and editing visits.
        \n\n###_**Roles**_
        \n\n**Manager**:
        \n- Full access on web and mobile: create, read, update, and delete visits.
        \n\n**Planners**:
        \n- Can create, view, and edit visits on web and mobile.
        \n- Cannot delete visits.
        \n\n**Users** (outreach workers or volunteers):
        \n- Can create, edit, and delete their own visits on mobile.
        \n- View all visits for their organization.\n\n`,

      // Related Components
      `\n- **[SearchBar](http://localhost:6006/?path=/docs/components-searchbar-search--docs)**: Search and filter visits.
        \n- **[Breadcrumbs](http://localhost:6006/?path=/docs/components-breadcrumbs--docs)**: Provides navigational context.
        \n- **[VisitsTable](http://localhost:6006/?path=/docs/components-visits-visitstable--docs)**: Displays visits in table format.
        \n- **[VisitCards](http://localhost:6006/?path=/docs/components-visits-visitcards--docs)**: Displays visits in card/gallery format.
        \n- **[Pagination](http://localhost:6006/?path=/docs/components-pagination-pagination--docs)**: Handles paginated navigation.
        \n- **[EmptyState](http://localhost:6006/?path=/docs/components-emptystate--docs)**: Displays when no visits are found.
        \n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Icons used for visual enhancement to displays the page icon at the top left of the page.\n\n`,

    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'The organization ID used to fetch visits data.',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
  },
};

const Template = (args) => <VisitsPage {...args} />;

export const DefaultVisitsPage = Template.bind({});
DefaultVisitsPage.args = {
  organizationId: 1,
};
DefaultVisitsPage.decorators = [
  (Story) => {
    // Ensure the dummy data mock is used
    useFetchVisitsModule.default = mockUseFetchVisitsWithDummyData;
    return <Story />;
  },
];
DefaultVisitsPage.parameters = generateStoryDocs(
  // When is this variant used?
  `\n**Default VisitsPage**
      \n- Displays the VisitsPage with dummy data in Storybook (as API calls are not possible in Storybook).
      \n- The \`organizationId\` must be provided for fetching visits in a real scenario.
    \n\n**Includes:**
      \n- Search functionality.
      \n- Breadcrumbs for navigation.
      \n- View-switching between table and gallery modes.
      \n- Pagination for navigating through records.\n\n`,
  // Where is it currently used on the product?
  'User can navigate to this page using `Dashboard menu` --> [Visits](http://127.0.0.1:3000/visits)',
);

export const EmptyVisitsPage = Template.bind({});
EmptyVisitsPage.args = {
  organizationId: 1,
};
EmptyVisitsPage.decorators = [
  (Story) => {
    useFetchVisitsModule.default = mockUseFetchVisitsWithEmptyData;
    return <Story />;
  },
];

EmptyVisitsPage.parameters = {
  ...generateStoryDocs(
    // When is this variant used?
    `\n**Empty VisitsPage**
        \n- Displays the VisitsPage with no visits data to simulate the \`EmptyState\` component.
      \n\n**Includes:**
        \n- Search functionality.
        \n- Breadcrumbs for navigation.
        \n- EmptyState component when no visits are available.\n\n`,
    // Where is it currently used on the product?
    'Empty visits scenario is shown when no data is returned from the API or search filters result in no matches.',
  ),
  docs: {
    disable: true,
  },
};
